import { RawAssemblyTree } from '@/state'

type Node = { uuid: string; parent?: string; children: string[] }

type TreeIndex = {
  byId: Map<string, Node>
  children: Map<string, string[]>
  parent: Map<string, string | undefined>
  versionKey: string // anything that changes when the tree changes
}

export function indexAssemblyTree(assemblyTree: RawAssemblyTree): TreeIndex {
  const byId = new Map<string, Node>()
  const children = new Map<string, string[]>()
  const parent = new Map<string, string | undefined>()

  for (const n of assemblyTree.nodes as Node[]) {
    byId.set(n.uuid, n)
    children.set(n.uuid, n.children ?? [])
    parent.set(n.uuid, n.parent)
  }

  // if you have a stable id/version for the tree, use it here
  return { byId, children, parent, versionKey: String(assemblyTree.root) }
}

// module-level caches (optional, but cheap & effective)
const treeIndexCache = new WeakMap<RawAssemblyTree, TreeIndex>()
const visiblePathCache = new Map<string, Set<string>>() // key by (treeVersion + groupIds)

function getTreeIndex(assemblyTree: RawAssemblyTree): TreeIndex {
  let idx = treeIndexCache.get(assemblyTree)
  if (!idx) {
    idx = indexAssemblyTree(assemblyTree)
    treeIndexCache.set(assemblyTree, idx)
  }
  return idx
}

export function getVisiblePathOfGroupIdsFast({
  assemblyGroupIds,
  assemblyTree,
}: {
  assemblyGroupIds: string[]
  assemblyTree: RawAssemblyTree
}): Set<string> {
  const { byId, parent, children, versionKey } = getTreeIndex(assemblyTree)

  // Stable cache key (tree version + sorted group ids to avoid order cache misses)
  const key = `${versionKey}|${[...(assemblyGroupIds ?? [])].sort().join(',')}`
  const cached = visiblePathCache.get(key)
  if (cached) return cached

  const result = new Set<string>()
  const startIds = new Set(assemblyGroupIds ?? [])

  // Walk ancestors and descendants only once per starting id.
  for (const id of startIds) {
    const start = byId.get(id)
    if (!start) continue

    // 1) add the node itself
    if (!result.has(id)) result.add(id)

    // 2) ancestors
    let p = parent.get(id)
    while (p && p !== assemblyTree.root) {
      if (result.has(p)) break
      result.add(p)
      p = parent.get(p)
    }

    // 3) descendants (BFS)
    const q: string[] = children.get(id)
      ? [...(children.get(id) as string[])]
      : []
    while (q.length) {
      const child = q.pop()!
      if (result.has(child)) continue
      result.add(child)
      const kids = children.get(child)
      if (kids && kids.length) q.push(...kids)
    }
  }

  visiblePathCache.set(key, result)
  return result
}
